import { GraphQLClient } from "graphql-request";
import { getDatoCMSEnvironment } from "utils/datocms-environment";
import { logToDataDog } from "@pepdirect/v3/helpers/logging";
import { endpoints } from "config";

export function createDatoClient(preview = false): GraphQLClient {
  const endpoint = preview
    ? `${endpoints.datoCmsEndpoint}/preview`
    : `${endpoints.datoCmsEndpoint}/`;

  const datoEnv = getDatoCMSEnvironment();

  const client = new GraphQLClient(endpoint, {
    headers: {
      ...(datoEnv && { "X-Environment": datoEnv }),
      "X-Cache-Tags": "true",
    },
    responseMiddleware: async (response) => {
      if (response instanceof Error) {
        logToDataDog(response);
        return;
      }
      // @ts-expect-error GQLErrors are appearing inside `response.response.errors`
      const errors = response?.errors ?? response?.response?.errors;
      if (errors) {
        logToDataDog(errors);
      }
    },
  });

  return client;
}

/**
 * Creates a DatoCMS client that captures request and response headers
 * This is a drop-in replacement for createDatoClient that adds header information
 * @param preview Whether to use the preview endpoint
 * @param queryParams Optional query parameters to append to the endpoint
 * @param outputHeaders Optional object to store response headers
 * @returns A GraphQLClient with additional header information
 */
export function createDetailedDatoClient(
  preview = false,
  queryParams?: string,
  outputHeaders?: Record<string, string>
): GraphQLClient {
  const endpoint = preview
    ? `${endpoints.datoCmsEndpoint}/preview${
        queryParams ? `?${queryParams}` : ""
      }`
    : `${endpoints.datoCmsEndpoint}/${queryParams ? `?${queryParams}` : ""}`;

  const datoEnv = getDatoCMSEnvironment();

  // Create a custom fetch function that captures headers
  const customFetch = async (url: string, options: RequestInit) => {
    const response = await fetch(url, options);

    // Store response headers
    response.headers.forEach((value, key) => {
      if (outputHeaders) {
        outputHeaders[key] = value;
      }
    });

    return response;
  };

  // Create the client with the custom fetch
  const client = new GraphQLClient(endpoint, {
    headers: {
      ...(datoEnv && { "X-Environment": datoEnv }),
      "X-Cache-Tags": "true",
    },
    fetch: customFetch,
  });

  return client;
}
